import { Link } from "@tanstack/react-router"
import { createColumnHelper, flexRender, Header, useTable } from "@tanstack/react-table"
import { Badge } from "design-system/atoms/Badge"
import { Button } from "design-system/atoms/Button"
import DateDisplay from "design-system/molecules/DateDisplay"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { cn } from "design-system/utils"
import { features, type Features } from "lib/table"
import { Archive, ArchiveRestore, ArrowUpRight, Unlink } from "lucide-react"
import { useArchiveWhatsappOpeningMessageMutation } from "modules/campaigns/hooks/sections/openingMessages/useArchiveWhatsappOpeningMessageMutation"
import { useUpdateWhatsappOpeningMessageMutation } from "modules/campaigns/hooks/sections/openingMessages/useUpdateWhatsappOpeningMessageMutation"
import { useWhatsappOpeningMessagesQuery } from "modules/campaigns/hooks/sections/openingMessages/useWhatsappOpeningMessagesQuery"
import Locale from "modules/shared/components/Miscelanea/Locale"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
  WhatsappOpeningMessageResponseData,
  WhatsappOpeningMessagesResponseData,
  WhatsappOpeningMessagesSortSchema,
} from "schema"
import { z } from "zod"

const columnHelper = createColumnHelper<
  Features,
  z.infer<typeof WhatsappOpeningMessagesResponseData>[number]
>()

type WhatsappOpeningMessagesSortFields = z.infer<
  typeof WhatsappOpeningMessagesSortSchema
>["sortBy"]["field"]

type Props = {
  data: ReturnType<typeof useWhatsappOpeningMessagesQuery>["data"]
  isPending: boolean
  selectedIds?: string[]
  initialSelectedIds?: string[]
  onSelectionChange?: (ids: string[]) => void
  fields: WhatsappOpeningMessagesSortFields[]
  onSort: (field: WhatsappOpeningMessagesSortFields) => void
  search: {
    field: WhatsappOpeningMessagesSortFields
    order: "asc" | "desc"
  }
}

export const WhatsappOpeningMessagesTable = ({
  data,
  isPending,
  fields,
  onSort,
  search,
}: Props) => {
  const { t } = useTranslation()
  const { mutateAsync: mutateAsyncArchive, isPending: archiveIsPending } =
    useArchiveWhatsappOpeningMessageMutation()
  const { mutateAsync: mutateAsyncUnArchive, isPending: unArchiveIsPending } =
    useUpdateWhatsappOpeningMessageMutation()

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor("identifier", {
          header: t("campaigns.templates.openingMessages.table.identifier"),
          meta: {
            colSpan: 2,
          },
        }),
        columnHelper.accessor("text", {
          header: t("campaigns.templates.openingMessages.table.text"),
        }),
        columnHelper.accessor("locale", {
          header: t("campaigns.templates.openingMessages.table.locale"),
          cell: ({ getValue }) => {
            const value = getValue()
            return value ? <Locale code={value} /> : "-"
          },
        }),
        columnHelper.accessor("campaign", {
          header: t("campaigns.templates.openingMessages.table.campaign"),
          cell: (props) => {
            const campaign = props.getValue()

            if (!campaign || !campaign.id) {
              return (
                <Badge variant={"outline"}>
                  <Unlink size={14} />
                  {t("campaigns.templates.openingMessages.table.noCampaign")}
                </Badge>
              )
            }

            const name = campaign.name

            return (
              <Link
                className={cn(
                  "flex font-medium hover:underline",
                  name ? "text-interactive" : "text-muted-foreground",
                )}
                to="/campaigns/$id"
                params={{ id: campaign.id }}
                target="_blank"
              >
                <span>{name ?? t("campaigns.untitled")}</span>
                <ArrowUpRight size={16} />
              </Link>
            )
          },
          meta: {
            colSpan: 2,
          },
        }),
        columnHelper.accessor("createdAt", {
          header: t("campaigns.templates.openingMessages.table.createdAt"),
          cell: ({ row }) => {
            return <DateDisplay date={row.original.createdAt} />
          },
          meta: {
            align: "right",
          },
        }),
        columnHelper.display({
          id: "actions",
          cell: (props) => {
            const { id, archivedAt, identifier, locale, text } = props.row.original

            return (
              <div className="flex gap-1.5 justify-end opacity-0 group-hover/row:opacity-100">
                {archivedAt ? (
                  <Button
                    variant={"outline"}
                    size="icon"
                    onClick={(ev) => {
                      ev.stopPropagation()
                      mutateAsyncUnArchive({ id, identifier, locale, text })
                    }}
                    disabled={unArchiveIsPending}
                  >
                    <ArchiveRestore />
                  </Button>
                ) : (
                  <Button
                    variant={"outline"}
                    size="icon"
                    onClick={(ev) => {
                      ev.stopPropagation()
                      mutateAsyncArchive(id)
                    }}
                    disabled={archiveIsPending}
                  >
                    <Archive />
                  </Button>
                )}
              </div>
            )
          },
        }),
      ]),
    [t, mutateAsyncArchive, archiveIsPending, mutateAsyncUnArchive, unArchiveIsPending],
  )

  const table = useTable({
    data: data ?? [],
    columns,
    features,
  })

  function renderContent() {
    if (isPending || data === undefined) {
      return <TableSkeleton colSpans={columns.map((c) => c.meta?.colSpan ?? 1)} />
    }

    if (data.length === 0) {
      return (
        <TableEmptySlate
          colSpan={columns.reduce((acc, c) => acc + (c.meta?.colSpan ?? 1), 0)}
          message={t("campaigns.templates.openingMessages.table.empty")}
        />
      )
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow
        key={row.original.id}
        navigate={{
          to: "/settings/channels/whatsapp/openings/$id",
          params: { id: row.original.id },
        }}
        className="group/row"
      >
        {row.getVisibleCells().map((cell) => (
          <TableCell
            key={cell.id}
            className={cn("cursor-pointer", cell.column.columnDef.meta?.className)}
            {...cell.column.columnDef.meta}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  function buildSortable(
    header: Header<Features, z.infer<typeof WhatsappOpeningMessageResponseData>, unknown>,
  ) {
    if (!fields.includes(header.id as WhatsappOpeningMessagesSortFields)) return

    const isActive = search.field === header.id

    return {
      active: isActive,
      direction: isActive ? search.order : "asc",
      onClick: () => onSort(header.id as WhatsappOpeningMessagesSortFields),
    }
  }

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead
                key={header.id}
                sortable={buildSortable(header)}
                {...header.column.columnDef.meta}
              >
                {flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody className="**:data-[slot=table-cell]:first:w-8">{renderContent()}</TableBody>
    </Table>
  )
}
